/** Post-purchase screen helpers for HATDEV-99283 / HATDEV-99287 acceptance criteria. */

import {
  isProceedClaimedForContext,
  markProceedClaimedForContext,
  readSessionFlag,
  writeSessionFlag,
} from "src/app/services/syndicate-join-idempotency";

export {
  JOIN_ENDPOINT_CLAIM_STALE_MS,
  applyJoinBackendIdempotencyStatus,
  buildCreditJoinContextKey,
  buildJoinIdempotencyKey,
  clearInFlightJoinRequest,
  ensureJoinIdempotencyKey,
  getBrowserSessionStorage,
  getInFlightJoinRequest,
  interpretJoinBackendIdempotencyStatus,
  isJoinCompletedForContext,
  isJoinEndpointClaimStale,
  isProceedClaimedForContext,
  joinCompletedSessionKey,
  joinEndpointClaimSessionKey,
  joinIdempotencySessionKey,
  markJoinCompletedForContext,
  markProceedClaimedForContext,
  proceedClaimedSessionKey,
  readJoinEndpointClaimTimestamp,
  readSessionFlag,
  readSessionValue,
  releaseJoinEndpointClaim,
  resetJoinIdempotencyMemoryForTests,
  setInFlightJoinRequest,
  shouldProbeJoinIdempotencyBackendOnRefresh,
  tryClaimJoinEndpointGuard,
  writeSessionFlag,
  writeSessionValue,
} from "src/app/services/syndicate-join-idempotency";

export const POST_PURCHASE_SLOW_STATUS_MS = 5000;
/**
 * Hard wall-clock cap for the post-purchase success loader (AC: ≤2s).
 * After this budget, spinner/progress must dismiss and a clear CTA / Almost there state shows —
 * even if tickets are still resolving. Auto-nav paint delay must also stay under this budget.
 */
export const POST_PURCHASE_SUCCESS_LOADER_MAX_MS = 2000;
/** Overall max wait while polling for syndicate request / payment status. */
export const SYNDICATE_REQUEST_POLL_MAX_MS = 50000;

export const CREDIT_PURCHASE_SUCCESS_EVENT = "credit_purchase_success";
export const SYNDICATE_JOIN_INITIATED_EVENT = "syndicate_join_initiated";
export const SYNDICATE_JOIN_SUCCESS_EVENT = "syndicate_join_success";

const ANALYTICS_KEY_PREFIX = "hatdev_post_purchase_analytics_";

/** In-memory dedupe when sessionStorage is missing or setItem fails. */
const analyticsMemoryClaims = new Set<string>();

/**
 * Same-session unlock markers for analytics claims.
 * Needed when release clears memory but sessionStorage.removeItem fails — claim
 * ORs memory + session flag, so without this override a stale session flag would
 * keep blocking retries in the same page session.
 */
const analyticsEmitClaimsReleased = new Set<string>();

export function buildPostPurchaseContextKey(parts: {
  customerPurchaseId?: string | null;
  requestId?: string | null;
  encryptSyndicateId?: string | null;
  encryptedId?: string | null;
}): string {
  return (
    parts.customerPurchaseId ||
    parts.requestId ||
    parts.encryptSyndicateId ||
    parts.encryptedId ||
    "unknown"
  );
}

export function analyticsSessionKey(contextKey: string, eventName: string): string {
  return `${ANALYTICS_KEY_PREFIX}${contextKey}_${eventName}`;
}

/**
 * Returns true if this is the first emit for the event+context.
 * Uses sessionStorage when available, with an in-memory fallback so write failures
 * cannot cause duplicate emits in the same page lifetime.
 */
export function claimAnalyticsEmitOnce(
  storage: Storage | null | undefined,
  contextKey: string,
  eventName: string
): boolean {
  const key = analyticsSessionKey(contextKey, eventName);
  const releasedThisSession = analyticsEmitClaimsReleased.delete(key);
  if (
    analyticsMemoryClaims.has(key) ||
    (!releasedThisSession && readSessionFlag(storage, key))
  ) {
    return false;
  }
  analyticsMemoryClaims.add(key);
  writeSessionFlag(storage, key);
  return true;
}

/**
 * Unlock an analytics claim after a failed trackEvent so a retry can emit once.
 * Does not affect other events for the same purchase context.
 */
export function releaseAnalyticsEmitClaim(
  storage: Storage | null | undefined,
  contextKey: string,
  eventName: string
): void {
  const key = analyticsSessionKey(contextKey, eventName);
  analyticsMemoryClaims.delete(key);
  // Mark released before removeItem so same-session retry stays unlocked even if
  // storage removal throws (claim ORs memory + session flag).
  analyticsEmitClaimsReleased.add(key);
  try {
    storage?.removeItem(key);
  } catch {
    // Best-effort. analyticsEmitClaimsReleased keeps same-session reclaim open.
  }
}

/** Test helper — clear in-memory analytics claims between specs. */
export function resetAnalyticsMemoryClaimsForTests(): void {
  analyticsMemoryClaims.clear();
  analyticsEmitClaimsReleased.clear();
}

export function isLoadingUiActive(showLoader: boolean, showProgressBar: boolean): boolean {
  return showLoader || showProgressBar;
}

export function shouldShowProceedCta(options: {
  syndicateTicketSource: string | null | undefined;
  showLoader: boolean;
  showProgressBar: boolean;
  dataFetched: boolean;
  showAlmostThere: boolean;
  showTimerError: boolean;
}): boolean {
  if (options.syndicateTicketSource === "syndicateTab") {
    return false;
  }
  if (isLoadingUiActive(options.showLoader, options.showProgressBar)) {
    return false;
  }
  return options.dataFetched || options.showAlmostThere || options.showTimerError;
}

/**
 * First Proceed / auto-nav wins; subsequent calls are no-ops.
 * When storage + contextKey are provided, the claim survives refresh for that purchase.
 */
export function tryClaimProceedSlot(
  state: {
    proceedNavigating: boolean;
    hasAutoNavigated: boolean;
  },
  storage?: Storage | null,
  contextKey?: string
): { claimed: boolean; proceedNavigating: boolean; hasAutoNavigated: boolean } {
  if (contextKey && isProceedClaimedForContext(storage ?? null, contextKey)) {
    return {
      claimed: false,
      proceedNavigating: true,
      hasAutoNavigated: true,
    };
  }
  if (state.proceedNavigating || state.hasAutoNavigated) {
    return {
      claimed: false,
      proceedNavigating: state.proceedNavigating,
      hasAutoNavigated: state.hasAutoNavigated,
    };
  }
  if (contextKey) {
    markProceedClaimedForContext(storage ?? null, contextKey);
  }
  return { claimed: true, proceedNavigating: true, hasAutoNavigated: true };
}

/** Short paint delay after loader dismiss; always ≤ success-loader budget. */
export function autoNavigateDelayMs(successLoaderMaxMs = POST_PURCHASE_SUCCESS_LOADER_MAX_MS): number {
  return Math.min(300, successLoaderMaxMs);
}

/**
 * Wall-clock ms before the post-purchase success loader must dismiss
 * (Almost there + Proceed). Enforces the ≤2s AC independently of the 5s slow-status SLA.
 */
export function postPurchaseLoaderHardCapMs(
  successLoaderMaxMs = POST_PURCHASE_SUCCESS_LOADER_MAX_MS
): number {
  return successLoaderMaxMs;
}

/** Credit-purchase analytics only — exclude cash / add-card / sales-journey pays. */
export function shouldEmitCreditPurchaseSuccess(flags: {
  isPostPurchase: boolean;
  isSalesJourney: boolean;
  isCashPurchase: boolean;
  isAddCardCashPurchase: boolean;
}): boolean {
  return (
    flags.isPostPurchase &&
    !flags.isSalesJourney &&
    !flags.isCashPurchase &&
    !flags.isAddCardCashPurchase
  );
}
